import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BiddingApprovalMode } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import { JEPX_AREA_CODES } from '~/models/area'

/**
 * 提出確認モーダルの確認項目「承認フロー」 に出す、 承認モード 1 種類ぶんのエリア群
 * (#9586)。 `mode` は要承認 / 承認なし / 自動 のいずれか (UNSPECIFIED は持たない)。
 */
export type ApprovalModeGroup = {
  mode: BiddingApprovalMode
  areaCodes: readonly AreaCode[]
}

/** 並び順。 design `js/approval-modes.js` の `_approvalModeText` と同じく 要承認 → 承認なし → 自動。 */
const MODE_ORDER: readonly BiddingApprovalMode[] = [
  BiddingApprovalMode.APPROVAL,
  BiddingApprovalMode.NO_APPROVAL,
  BiddingApprovalMode.AUTO,
]

/**
 * 提出対象のエリアを承認モードごとにまとめる (#9586、 スポット通常・ブロック / 時間前の
 * 提出確認モーダルで共通)。 1 種類なら要素 1 つ、 エリアで混ざる時はモードごとに並ぶ。
 *
 * - 同じエリアが何度出ても 1 回に畳む (札の数だけ渡ってくるため)
 * - エリアの並びは JEPX の 9 エリア順 (北 → 南、 `JEPX_AREA_CODES`)。 呼び出し元の札の
 *   並びに左右されないようにする
 * - 承認モードが UNSPECIFIED のエリアは要承認に含める (design の `_approvalModeText` と同じ
 *   扱い。 承認を経ずに送ると読ませない側に倒す)
 */
export function groupAreasByApprovalMode(
  areaCodes: readonly AreaCode[],
  modeOf: (area: AreaCode) => BiddingApprovalMode,
): readonly ApprovalModeGroup[] {
  const unique = new Set(areaCodes)
  const ordered = [
    ...JEPX_AREA_CODES.filter((code) => unique.has(code)),
    ...[...unique].filter((code) => !JEPX_AREA_CODES.includes(code)),
  ]
  const byMode = new Map<BiddingApprovalMode, AreaCode[]>()
  for (const area of ordered) {
    const raw = modeOf(area)
    const mode = MODE_ORDER.includes(raw) ? raw : BiddingApprovalMode.APPROVAL
    byMode.set(mode, [...(byMode.get(mode) ?? []), area])
  }
  return MODE_ORDER.flatMap((mode) => {
    const areas = byMode.get(mode)
    return areas === undefined ? [] : [{ mode, areaCodes: areas }]
  })
}
